import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MasonryFileList from './MasonryFileList';
import ListFileList from './ListFileList';
import { PaginationTypeResponse } from '../../api/types';

import useApi from '../../api';
import { useResponseHandler } from '../../utils/response';
import { useScrollContext } from '../../contexts/ScrollContext';
import { EVENTS, useFileDialog } from './CmsFileDialogs';
import { FileMetadata } from '../../api/types/cms';

export type CmsFileListRef = {
    load: () => void;
};

type CmsFileListProps = {
    mode: 'masonry' | 'list';
    listRef?: React.MutableRefObject<CmsFileListRef | undefined>;
    searchQuery?: string;
    poolId?: string;
    contentName?: string;
    onClick?: (item: FileMetadata) => void;
    mediaQueries?: Array<string>;
    selectedFiles?: Array<FileMetadata>;
};

const defaultPaging = {
    data: [],
    max_length: 0,
    limit: 20,
    has_next: true,
    length: 0,
    offset: 0,
    next_offset: 20,
};

export default function CmsFileList({
    listRef,
    searchQuery,
    mode,
    poolId,
    contentName,
    onClick,
    mediaQueries,
    selectedFiles = [],
}: CmsFileListProps) {
    const { cms, search } = useApi();
    const { openFileVersionDialog } = useFileDialog();
    const { handleResponse } = useResponseHandler();
    const { onScrollEndReached } = useScrollContext();

    const [pagination, setPagination] =
        useState<PaginationTypeResponse>(defaultPaging);

    /**
     * Loading file list
     */
    const loadFiles = useCallback(
        (limit: number = 20, offset: number = 0) => {
            if (searchQuery && searchQuery.length > 0) {
                search
                    .executeSearch(
                        searchQuery,
                        'filemetadata',
                        poolId,
                        contentName,
                        limit,
                        offset,
                    )
                    .then(response => {
                        handleResponse(response, () => {
                            if (response) {
                                if (response.offset === 0) {
                                    setPagination({
                                        ...response,
                                        data:
                                            response.results?.file_metadata ??
                                            [],
                                    });
                                } else {
                                    setPagination({
                                        ...response,
                                        data: [
                                            ...pagination.data,
                                            ...(response.results
                                                ?.file_metadata ?? []),
                                        ],
                                    });
                                }
                            }
                        });
                    });
            } else {
                cms.getFiles(limit, offset, poolId).then(response => {
                    handleResponse(response, () => {
                        if (response) {
                            if (response.offset === 0) {
                                setPagination(response);
                            } else {
                                setPagination({
                                    ...response,
                                    data: [
                                        ...pagination.data,
                                        ...response.data,
                                    ],
                                });
                            }
                        }
                    });
                });
            }
        },
        [search, cms, searchQuery, poolId, contentName, pagination],
    );

    /**
     * Setting listRef
     */
    useEffect(() => {
        if (listRef) {
            listRef.current = {
                load: () => loadFiles(),
            };
        }
    }, [listRef, loadFiles]);

    /**
     * Initial file load and pool change
     */
    useEffect(() => loadFiles(), [searchQuery, poolId]);

    /**
     * Adding scroll end reached listener
     */
    useEffect(() => {
        return onScrollEndReached(() => {
            if (pagination.has_next) {
                loadFiles(20, pagination.next_offset);
            }
        });
    }, [onScrollEndReached, loadFiles]);

    /**
     * Adding global event listener to force file list reload
     */
    useEffect(() => {
        window.addEventListener(EVENTS.list_reload, () => loadFiles());
        return () => {
            window.removeEventListener(EVENTS.list_reload, () => loadFiles());
        };
    }, [loadFiles]);

    return useMemo(() => {
        if (mode === 'masonry') {
            return (
                <MasonryFileList
                    items={pagination.data}
                    ease="power3.out"
                    duration={0.6}
                    stagger={0.05}
                    animateFrom="bottom"
                    scaleOnHover={true}
                    hoverScale={0.95}
                    blurToFocus={true}
                    colorShiftOnHover={true}
                    onClick={onClick ? onClick : openFileVersionDialog}
                    mediaQueries={mediaQueries}
                    selectedFiles={selectedFiles}
                />
            );
        }
        return (
            <ListFileList
                items={pagination.data}
                onClick={onClick ? onClick : openFileVersionDialog}
                selectedFiles={selectedFiles}
            />
        );
    }, [mode, selectedFiles, pagination.data]);
}
